import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  useWindowDimensions,
  TextInput,
  Modal,
  RefreshControl,
} from "react-native";
import {
  useNavigation,
  useRoute,
  RouteProp,
  useFocusEffect,
} from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import Svg, { Path, Circle } from "react-native-svg";
import { formatPct } from '../../utils/formatPercentage';
import { COLORS, FONTS } from "../../constants/config";
import {
  PageContainer,
  Navbar,
  Breadcrumb,
  KPIDistributionCard,
} from "../../components";
import TargetAchievementCard from "../../components/TargetAchievementCard";
import {
  useResponsive,
  useRoleNavigation,
  useAutoTranslate,
} from "../../hooks";
import { MainStackParamList } from "../../navigation/MainNavigator";
import { processesApi, subProcessesApi, goalsApi } from "../../services/apiService";
// FlowchartViewer no longer imported — Process no longer carries a drawing.
import { Process, SubProcess, KPIList } from "../../types";
import DeletionImpactModal from "../../components/DeletionImpactModal";
import DeletionApprovalBanner from "../../components/DeletionApprovalBanner";
import { useNotification } from "../../contexts/NotificationContext";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { useYear } from "../../contexts/YearContext";

type RouteProps = RouteProp<MainStackParamList, "ProcessDetail">;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({
  size = 16,
  color = "#6B7280",
}: {
  size?: number;
  color?: string;
}) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M15 18L9 12L15 6"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const EditIcon = ({
  size = 16,
  color = "#414042",
}: {
  size?: number;
  color?: string;
}) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const ExportIcon = ({
  size = 16,
  color = "#414042",
}: {
  size?: number;
  color?: string;
}) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M17 8L12 3L7 8"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M12 3V15"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const PlusIcon = ({
  size = 16,
  color = "#414042",
}: {
  size?: number;
  color?: string;
}) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 5V19M5 12H19"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const ArrowRightIcon = ({
  size = 16,
  color = "#414042",
}: {
  size?: number;
  color?: string;
}) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M5 12H19M19 12L12 5M19 12L12 19"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

// ============================================================
// Donut Chart Component
// ============================================================

interface DonutChartProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
}

/**
 * Get color based on achievement percentage
 * - >= 100%: Green (#22C55E) - ممتاز
 * - >= 80% and < 100%: Orange (#F97316) - جيد
 * - < 80%: Red (#EF4444) - يحتاج تحسين
 */
const getAchievementColor = (percentage: number): string => {
  if (percentage >= 100) return "#22C55E"; // Green - Excellent
  if (percentage >= 80) return "#F97316"; // Orange - Good
  return "#EF4444"; // Red - Needs Improvement
};

const DonutChart = ({
  percentage,
  size = 168,
  strokeWidth = 12,
}: DonutChartProps) => {
  const { colors, isDark } = useTheme();
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  // Dynamic color based on achievement percentage
  const progressColor = getAchievementColor(percentage);

  // Calculate dot position at the end of progress arc
  const angle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View
      style={{
        width: size,
        height: size,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={isDark ? "#374151" : "#E2E8F0"}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={progressColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${circumference}`}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot indicator at end of progress */}
        <Circle
          cx={dotX}
          cy={dotY}
          r={7.5}
          fill={progressColor}
          stroke={colors.card}
          strokeWidth={2}
        />
      </Svg>
      <View style={{ position: "absolute", alignItems: "center" }}>
        <Text
          style={{
            fontSize: 24,
            fontFamily: FONTS.numbers,
            fontWeight: "500",
            color: colors.textPrimary,
          }}
        >
          %{percentage}
        </Text>
      </View>
    </View>
  );
};

// ============================================================
// Progress Bar Component
// ============================================================

interface ProgressBarProps {
  current: number;
  total: number;
  color?: string;
}

const ProgressBar = ({
  current,
  total,
  color = "#2CA02C",
}: ProgressBarProps) => {
  const percentage = total > 0 ? Math.min((current / total) * 100, 100) : 0;

  return (
    <View style={progressStyles.container}>
      <View style={progressStyles.barBackground}>
        <View
          style={[
            progressStyles.barFill,
            { width: `${percentage}%`, backgroundColor: color },
          ]}
        />
      </View>
      <View style={progressStyles.dot} />
    </View>
  );
};

const progressStyles = StyleSheet.create({
  container: {
    flexDirection: "row-reverse",
    alignItems: "center",
    gap: 8,
  },
  barBackground: {
    flex: 1,
    height: 8,
    backgroundColor: "#E2E8F0",
    borderRadius: 4,
    overflow: "hidden",
  },
  barFill: {
    height: "100%",
    borderRadius: 4,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#2CA02C",
  },
});

// ============================================================
// Main Screen Component
// ============================================================

export default function ProcessDetailScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { id } = route.params;
  const { isMobile, isTablet, isDesktop, isSmallMobile, horizontalCardWidth } =
    useResponsive();
  const { showError, showSuccess } = useNotification();
  const { navigateToHome, navigateToProcesses, isViewOnly } = useRoleNavigation();
  const { user } = useAuth();
  // DTS 2.2: only Strategy Team (and superuser/tech admin) can soft-delete
  // operational items. Section Head's executive_items.create + .edit do NOT
  // include delete — that lives on hrcpms.soft_delete.
  const canSoftDelete = !!(
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.soft_delete')
  );
  // Proposing/editing operational items (sub-processes, KPIs) is governed by
  // `hrcpms.executive_items.create`, which Section Heads (رئيس قسم) hold. A
  // Strategy Section Head also has `strategic_goals.create`, which trips the
  // coarse isViewOnly flag — without this their Add/Edit buttons are hidden.
  const canCreateOperationalItem = !!(
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.executive_items.create')
  );
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const { translate, translateOrg } = useAutoTranslate();
  const { selectedYear } = useYear();

  // Theme colors for dark mode
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: isDark ? "#374151" : "#F3F4F6",
    sectionBg: isDark ? "#374151" : "#F1F5F9",
  };

  const [process, setProcess] = useState<Process | null>(null);
  const [subProcesses, setSubProcesses] = useState<SubProcess[]>([]);
  const [kpis, setKpis] = useState<KPIList[]>([]);
  const [loading, setLoading] = useState(true);
  const [goalAchievement, setGoalAchievement] = useState<number>(0);

  const [refreshing, setRefreshing] = useState(false);
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isOrgEmployee = user?.position_type === 'EMPLOYEE';
  const canApproveDeletion = (user?.is_superuser || user?.position_type === 'MANAGER' || user?.position_type === 'DIRECTOR' || (user?.permissions?.includes('hrcpms.strategic_goals.create') ?? false));

  // Responsive
  const isSmallScreen = isMobile;
  const isMediumScreen = isTablet;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [processData, subProcessesData] = await Promise.all([
        processesApi.get(id),
        subProcessesApi.list({ process: id, page_size: 100 }),
      ]);

      setProcess(processData);
      setSubProcesses(subProcessesData.results || []);
      // Use KPIs from main response instead of separate API call
      setKpis(processData.kpis || []);

      // Fetch strategic goal achievement if goal is linked
      if (processData.strategic_goal) {
        try {
          const goalData = await goalsApi.get(processData.strategic_goal);
          setGoalAchievement(goalData.achievement_percentage || 0);
        } catch (err) {
          // Goal fetch failed, use 0 as default
          setGoalAchievement(0);
        }
      }
    } catch (err: any) {
      showError(t("processes.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData]),
  );

  // Refetch when year changes
  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const handleEdit = () => {
    navigation.navigate("AddProcess", { processId: id });
  };

  const handleDelete = () => {
    setShowDeletionImpact(true);
  };

  const confirmDelete = async () => {
    try {
      setIsDeleting(true);
      const response = await processesApi.delete(id);
      if ((response as any)?.status === 202) {
        showSuccess(t('deletion.requestSent'));
      } else {
        showSuccess(t('processes.deleteSuccess'));
      }
      navigation.goBack();
    } catch (error: any) {
      if (error.response?.status === 202) {
        showSuccess(t('deletion.requestSent'));
        navigation.goBack();
      } else if (error.response?.status === 409) {
        showError(t('deletion.alreadyPending'));
      } else {
        showError(t('messages.deleteFailed'));
      }
    } finally {
      setIsDeleting(false);
      setShowDeletionImpact(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchData().finally(() => setRefreshing(false));
  };

  const handleExportPDF = () => {
    // TODO: Implement PDF export
    showError(t("common.exportFeatureComingSoon"));
  };

  const handleAddSubProcess = () => {
    navigation.navigate("AddSubProcess", { processId: id });
  };

  const handleAddKPI = () => {
    navigation.navigate("AddProcess", { processId: id, scrollToKpi: true });
  };

  const handleViewKPIDetails = (kpiId: number) => {
    navigation.navigate("KPIDetail", { id: kpiId });
  };

  // Calculate achievement percentage from KPIs
  // null when no KPIs — shows "--" instead of 0%
  const calculateAchievementPercentage = (): number | null => {
    if (kpis.length === 0) return null;

    let totalAchievement = 0;
    kpis.forEach((kpi) => {
      totalAchievement += kpi.achievement_percentage || 0;
    });

    return parseFloat((totalAchievement / kpis.length).toFixed(2));
  };

  const achievementPercentage = calculateAchievementPercentage();

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
          <Text style={[styles.loadingText, { color: colors.textMuted }]}>
            {t("processes.loadingProcessData")}
          </Text>
        </View>
      </PageContainer>
    );
  }

  if (!process) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <Text style={[styles.errorText, { color: colors.textMuted }]}>
            {t("processes.notFound")}
          </Text>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Text style={styles.backButtonText}>{t("common.back")}</Text>
          </TouchableOpacity>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        <View
          style={[styles.container, isSmallScreen && styles.containerSmall]}
        >
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: t("navigation.home") },
              { label: t("navigation.processes"), route: "Processes" },
              { label: t("processes.processDetails") },
            ]}
          />

          {/* Header Section */}
          <View style={styles.headerSection}>
            <View
              style={[
                styles.headerContainer,
                {
                  backgroundColor: themeColors.card,
                  borderColor: themeColors.cardBorder,
                },
              ]}
            >
              <View style={styles.headerTitleRow}>
                <View style={styles.headerActions}>
                  <TouchableOpacity
                    style={[
                      styles.exportButton,
                      {
                        backgroundColor: themeColors.sectionBg,
                        borderColor: themeColors.border,
                      },
                    ]}
                    onPress={handleExportPDF}
                  >
                    <Text
                      style={[
                        styles.exportButtonText,
                        { color: themeColors.textPrimary },
                      ]}
                    >
                      {t("common.exportPDF")}
                    </Text>
                    <ExportIcon size={13} color={themeColors.iconDefault} />
                  </TouchableOpacity>
                  {/* Hide edit/delete buttons for view-only users */}
                  {(canCreateOperationalItem || !isViewOnly) && (
                    <>
                      {canSoftDelete && (
                        <TouchableOpacity
                          style={[
                            styles.editButton,
                            {
                              backgroundColor: '#FEE2E2',
                              borderColor: '#FECACA',
                            },
                          ]}
                          onPress={handleDelete}
                        >
                          <Text style={{ fontSize: 13, color: '#DC2626', fontFamily: FONTS.medium }}>
                            {t("common.delete")}
                          </Text>
                        </TouchableOpacity>
                      )}
                      <TouchableOpacity
                        style={[
                          styles.editButton,
                          {
                            backgroundColor: themeColors.sectionBg,
                            borderColor: themeColors.border,
                          },
                        ]}
                        onPress={handleEdit}
                      >
                        <Text
                          style={[
                            styles.editButtonText,
                            { color: themeColors.textPrimary },
                          ]}
                        >
                          {t("common.edit")}
                        </Text>
                        <EditIcon size={13} color={themeColors.iconDefault} />
                      </TouchableOpacity>
                    </>
                  )}
                </View>
                <View style={styles.headerTitleWrapper}>
                  <Text
                    style={[
                      styles.headerTitle,
                      { color: themeColors.textPrimary },
                    ]}
                  >
                    {translate(process.name)}
                  </Text>
                  <Text
                    style={[
                      styles.headerDescription,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {translate(process.description) ||
                      t("common.noDescription")}
                  </Text>
                </View>
              </View>
            </View>
          </View>

          {/* Pending Deletion Banner */}
          {(process as any).pending_deletion_info && (
            <DeletionApprovalBanner
              deletionInfo={(process as any).pending_deletion_info}
              canApprove={canApproveDeletion}
              canCancel={isOrgEmployee && (process as any).pending_deletion_info.requested_by_id === user?.id}
              onApproved={() => { showSuccess(t('deletion.approved')); navigation.goBack(); }}
              onRejected={() => { showSuccess(t('deletion.rejected')); fetchData(); }}
              onCancelled={() => { showSuccess(t('deletion.requestCancelled')); fetchData(); }}
              onViewImpact={() => setShowDeletionImpact(true)}
            />
          )}

          {/* Main Content Row - KPI Distribution, Donut Chart, and Basic Info */}
          <View
            style={[
              styles.mainContentRow,
              isSmallScreen && styles.mainContentRowSmall,
            ]}
          >
            {/* Left - Basic Information Section */}
            <View
              style={[
                styles.basicInfoSection,
                {
                  backgroundColor: themeColors.card,
                  borderColor: themeColors.cardBorder,
                },
                isSmallScreen && styles.basicInfoSectionSmall,
              ]}
            >
              <Text
                style={[
                  styles.basicInfoTitle,
                  { color: themeColors.textPrimary },
                ]}
              >
                {t("common.basicInfo")}
              </Text>

              <View style={styles.infoGrid}>
                {/* Row 1 */}
                <View style={styles.infoRow}>
                  <View
                    style={[
                      styles.infoItem,
                      { backgroundColor: themeColors.sectionBg },
                    ]}
                  >
                    <Text
                      style={[
                        styles.infoLabel,
                        { color: themeColors.textMuted },
                      ]}
                    >
                      {t("common.classification")}
                    </Text>
                    <Text
                      style={[
                        styles.infoValue,
                        { color: themeColors.textPrimary },
                      ]}
                    >
                      {t("common.main")}
                    </Text>
                  </View>
                </View>
                {/* Row 2 */}
                <View style={styles.infoRow}>
                  <View
                    style={[
                      styles.infoItem,
                      { backgroundColor: themeColors.sectionBg },
                    ]}
                  >
                    <Text
                      style={[
                        styles.infoLabel,
                        { color: themeColors.textMuted },
                      ]}
                    >
                      {t("unitGoals.organizationalUnit")}
                    </Text>
                    <Text
                      style={[
                        styles.infoValue,
                        { color: themeColors.textPrimary },
                      ]}
                    >
                      {(process.organizational_units_details || []).map(u => translate(u.name_ar || u.name)).join('، ') || "-"}
                    </Text>
                  </View>
                </View>
              </View>
            </View>

            {/* Middle - Performance Indicators - Donut Chart */}
            <View
              style={[
                styles.donutSection,
                {
                  backgroundColor: themeColors.card,
                  borderColor: themeColors.cardBorder,
                },
                isSmallScreen && styles.donutSectionSmall,
              ]}
            >
              <Text
                style={[
                  styles.sectionLabel,
                  { color: themeColors.textPrimary },
                ]}
              >
                {t("kpis.performanceIndicators")}
              </Text>
              {achievementPercentage != null ? (
                <DonutChart
                  percentage={achievementPercentage}
                  size={168}
                  strokeWidth={14}
                />
              ) : (
                <View style={{ width: 168, height: 168, justifyContent: 'center', alignItems: 'center' }}>
                  <Text style={{ fontSize: 36, fontWeight: '700', color: themeColors.textMuted }}>--</Text>
                </View>
              )}
              {/* Legend Items */}
              <View style={styles.donutLegendItems}>
                <View style={styles.donutLegendRow}>
                  <View
                    style={[
                      styles.donutLegendDot,
                      { backgroundColor: "#22C55E" },
                    ]}
                  />
                  <Text
                    style={[
                      styles.donutLegendLabel,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("kpis.excellent")} (≥100%)
                  </Text>
                </View>
                <View style={styles.donutLegendRow}>
                  <View
                    style={[
                      styles.donutLegendDot,
                      { backgroundColor: "#F97316" },
                    ]}
                  />
                  <Text
                    style={[
                      styles.donutLegendLabel,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("kpis.good")} (≥80%)
                  </Text>
                </View>
                <View style={styles.donutLegendRow}>
                  <View
                    style={[
                      styles.donutLegendDot,
                      { backgroundColor: "#EF4444" },
                    ]}
                  />
                  <Text
                    style={[
                      styles.donutLegendLabel,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("kpis.needsImprovement")}
                  </Text>
                </View>
              </View>
            </View>

            {/* Right - KPI Distribution Card */}
            <View style={[styles.kpiDistributionWrapper, isSmallScreen && styles.kpiDistributionWrapperSmall]}>
              <KPIDistributionCard distribution={process.kpi_distribution} />
            </View>
          </View>

          {/* Process Connections Card - ارتباطات العملية */}
          <View
            style={[
              styles.connectionsCard,
              {
                backgroundColor: themeColors.card,
                borderColor: themeColors.cardBorder,
              },
            ]}
          >
            <Text
              style={[
                styles.connectionsCardTitle,
                { color: themeColors.textPrimary },
              ]}
            >
              {t("processes.processConnections")}
            </Text>

            {/* Flowchart viewer removed 2026-05-14 — drawings live on
                SubProcess. Process is shown as a list of its
                sub-processes underneath. */}

            {/* KPIs Section */}
            <View
              style={[
                styles.kpisSection,
                { backgroundColor: themeColors.sectionBg },
              ]}
            >
              <View style={styles.kpisSectionHeader}>
                {(canCreateOperationalItem || !isViewOnly) && (
                  <TouchableOpacity
                    style={[
                      styles.addKpiBtn,
                      { borderColor: themeColors.border },
                    ]}
                    onPress={handleAddKPI}
                  >
                    <Text
                      style={[
                        styles.addKpiBtnText,
                        { color: themeColors.textPrimary },
                      ]}
                    >
                      {t("kpis.addKPIForProcess")}
                    </Text>
                    <PlusIcon size={13} color={themeColors.iconDefault} />
                  </TouchableOpacity>
                )}
                <Text
                  style={[
                    styles.kpisSectionTitle,
                    { color: themeColors.textPrimary },
                  ]}
                >
                  {t("kpis.performanceIndicators")}
                </Text>
              </View>

              <View style={styles.rtlWrapper}>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={true}
                  contentContainerStyle={styles.horizontalCardsRow}
                  style={styles.horizontalScrollContainer}
                >
                  {kpis.length > 0 ? (
                    kpis.map((kpi) => {
                      const achievement = parseFloat(kpi.achievement_percentage || '0');
                      // Color based on achievement: green >= 80%, orange >= 50%, red < 50%
                      const progressColor =
                        achievement >= 80
                          ? "#2CA02C"
                          : achievement >= 50
                            ? "#EAB308"
                            : "#EF4444";

                      return (
                        <View
                          key={kpi.id}
                          style={[
                            styles.rtlCard,
                            { width: horizontalCardWidth },
                          ]}
                        >
                          <TargetAchievementCard
                            title={translate(kpi.name)}
                            achieved={kpi.latest_value || '0'}
                            target={kpi.target_value}
                            unit={kpi.unit === 'percentage' ? '%' : translate(kpi.unit === 'count' ? 'عدد' : kpi.unit)}
                            achievedColor={progressColor}
                            onViewDetails={() => handleViewKPIDetails(kpi.id)}
                          />
                        </View>
                      );
                    })
                  ) : (
                    <View style={[styles.emptyStateContainer, styles.rtlCard]}>
                      <Text
                        style={[
                          styles.emptyStateText,
                          { color: themeColors.textMuted },
                        ]}
                      >
                        {t("kpis.noKPIs")}
                      </Text>
                    </View>
                  )}
                </ScrollView>
              </View>
            </View>

            {/* Strategic Goals Section */}
            <View
              style={[
                styles.strategicGoalsSection,
                { backgroundColor: themeColors.sectionBg },
              ]}
            >
              <View style={styles.strategicGoalsSectionHeader}>
                <Text
                  style={[
                    styles.strategicGoalsSectionTitle,
                    { color: themeColors.textPrimary },
                  ]}
                >
                  {t("goals.strategicGoals")}
                </Text>
              </View>

              {process.strategic_goal_title ? (
                <View
                  style={[
                    styles.strategicGoalsGrid,
                    isSmallScreen && styles.strategicGoalsGridSmall,
                  ]}
                >
                  <TouchableOpacity
                    style={[
                      styles.strategicGoalCard,
                      isSmallScreen && styles.strategicGoalCardSmall,
                      {
                        backgroundColor: themeColors.card,
                        borderColor: themeColors.cardBorder,
                      },
                    ]}
                    onPress={() => {
                      console.log('Goal card pressed');
                      navigation.navigate("GoalDetail", {
                        id: process.strategic_goal!,
                        fromProcess: {
                          id: process.id,
                          name: process.name,
                        },
                      });
                    }}
                    activeOpacity={0.7}
                  >
                    <View style={styles.strategicGoalCardContent}>
                      <Text
                        style={[
                          styles.strategicGoalCardTitle,
                          { color: themeColors.textPrimary },
                        ]}
                      >
                        {translate(process.strategic_goal_title)}
                      </Text>
                      <View style={styles.subProcessProgressRow}>
                        <View
                          style={[
                            styles.progressBarContainer,
                            { backgroundColor: themeColors.sectionBg },
                          ]}
                        >
                          <View
                            style={[
                              styles.progressBarFill,
                              { width: `${Math.min(goalAchievement, 100)}%` },
                            ]}
                          />
                          <View
                            style={[
                              styles.progressDot,
                              {
                                right: `${Math.max(Math.min(goalAchievement, 100) - 5, 0)}%`,
                              },
                            ]}
                          />
                        </View>
                        <View
                          style={[
                            styles.progressBadge,
                            { backgroundColor: themeColors.sectionBg },
                          ]}
                        >
                          <Text
                            style={[
                              styles.progressBadgeText,
                              { color: themeColors.textPrimary },
                            ]}
                          >
                            %{goalAchievement}
                          </Text>
                        </View>
                      </View>
                    </View>
                    <View style={styles.viewDetailsBtn}>
                      <Text
                        style={[
                          styles.viewDetailsBtnText,
                          { color: themeColors.textPrimary },
                        ]}
                      >
                        {t("common.viewDetails")}
                      </Text>
                      <ChevronLeftIcon
                        size={16}
                        color={themeColors.textPrimary}
                      />
                    </View>
                  </TouchableOpacity>
                </View>
              ) : (
                <View style={styles.emptyStateContainer}>
                  <Text
                    style={[
                      styles.emptyStateText,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("processes.noStrategicGoal")}
                  </Text>
                </View>
              )}
            </View>

            {/* Sub-Processes Section */}
            <View
              style={[
                styles.subProcessesSection,
                { backgroundColor: themeColors.sectionBg },
              ]}
            >
              <View style={styles.subProcessesSectionHeader}>
                {(canCreateOperationalItem || !isViewOnly) && (
                  <TouchableOpacity
                    style={[
                      styles.addSubProcessBtn,
                      { borderColor: themeColors.border },
                    ]}
                    onPress={handleAddSubProcess}
                  >
                    <Text
                      style={[
                        styles.addSubProcessBtnText,
                        { color: themeColors.textPrimary },
                      ]}
                    >
                      {t("processes.addSubProcess")}
                    </Text>
                    <PlusIcon size={13} color={themeColors.iconDefault} />
                  </TouchableOpacity>
                )}
                <Text
                  style={[
                    styles.subProcessesSectionTitle,
                    { color: themeColors.textPrimary },
                  ]}
                >
                  {t("processes.subProcesses")}
                </Text>
              </View>

              {subProcesses.length > 0 ? (
                <View
                  style={[
                    styles.subProcessCardsGrid,
                    isSmallScreen && styles.subProcessCardsGridSmall,
                  ]}
                >
                  {subProcesses.map((subProcess) => {
                    // Progress based on KPI achievement percentage
                    const progressPercentage = Math.min(
                      subProcess.achievement_percentage || 0,
                      100
                    );

                    return (
                      <TouchableOpacity
                        key={subProcess.id}
                        style={[
                          styles.subProcessCardNew,
                          isSmallScreen && styles.subProcessCardNewSmall,
                          {
                            backgroundColor: themeColors.card,
                            borderColor: themeColors.cardBorder,
                          },
                        ]}
                        onPress={() =>
                          navigation.navigate("SubProcessDetail" as any, {
                            id: subProcess.id,
                          })
                        }
                        activeOpacity={0.7}
                      >
                        <View style={styles.subProcessCardContent}>
                          <Text
                            style={[
                              styles.subProcessCardTitle,
                              { color: themeColors.textPrimary, writingDirection: "rtl" },
                            ]}
                          >
                            {/* Stored as "العملية الفرعية - اسم العملية"; RTL so it
                                reads from the right: العملية الفرعية - اسم العملية */}
                            {translate(subProcess.name)}
                          </Text>
                          <View style={styles.subProcessProgressRow}>
                            <View
                              style={[
                                styles.progressBadge,
                                { backgroundColor: themeColors.sectionBg },
                              ]}
                            >
                              <Text
                                style={[
                                  styles.progressBadgeText,
                                  { color: themeColors.textPrimary },
                                ]}
                              >
                                {formatPct(progressPercentage)}%
                              </Text>
                            </View>
                            <View
                              style={[
                                styles.progressBarContainer,
                                { backgroundColor: themeColors.sectionBg },
                              ]}
                            >
                              <View
                                style={[
                                  styles.progressBarFill,
                                  { width: `${progressPercentage}%` },
                                ]}
                              />
                              <View
                                style={[
                                  styles.progressDot,
                                  {
                                    right: `${Math.max(progressPercentage - 5, 0)}%`,
                                  },
                                ]}
                              />
                            </View>
                          </View>
                        </View>
                        <View style={styles.viewDetailsBtn}>
                          <Text
                            style={[
                              styles.viewDetailsBtnText,
                              { color: themeColors.textPrimary },
                            ]}
                          >
                            {t("common.viewDetails")}
                          </Text>
                          <ChevronLeftIcon
                            size={16}
                            color={themeColors.textPrimary}
                          />
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              ) : (
                <View style={styles.emptyStateContainer}>
                  <Text
                    style={[
                      styles.emptyStateText,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("processes.noSubProcesses")}
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>
      </ScrollView>

      {/* Deletion Impact Modal */}
      <DeletionImpactModal
        visible={showDeletionImpact}
        onClose={() => setShowDeletionImpact(false)}
        onConfirm={confirmDelete}
        entityName={process?.name || ''}
        fetchImpact={async () => {
          const response = await processesApi.deletionImpact(id);
          return response.data;
        }}
        isOrgEmployee={isOrgEmployee}
        isSubmitting={isDeleting}
      />

    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
    padding: 24,
    paddingTop: 16,
  },
  containerSmall: {
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: COLORS.neutral600,
  },
  errorText: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: COLORS.neutral600,
    marginBottom: 16,
  },
  backButton: {
    backgroundColor: COLORS.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  backButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: "#FFFFFF",
  },

  // Breadcrumb
  breadcrumb: {
    flexDirection: "row-reverse",
    alignItems: "center",
    marginBottom: 16,
    gap: 8,
  },
  breadcrumbItem: {
    flexDirection: "row",
    alignItems: "center",
  },
  breadcrumbText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: "#6B7280",
  },
  breadcrumbActive: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: COLORS.primary,
  },

  // Header Section
  headerSection: {
    marginBottom: 24,
  },
  headerContainer: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    elevation: 2,
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
  },
  headerTitleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerTitleWrapper: {
    flex: 1,
    alignItems: "flex-end",
    gap: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontFamily: FONTS.medium,
    color: "#414042",
    textAlign: "right",
  },
  headerDescription: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: "#64748B",
    textAlign: "right",
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 20,
  },
  editButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    borderWidth: 1,
    borderColor: "#CBD5E1",
    paddingHorizontal: 12,
    paddingVertical: 5.5,
    borderRadius: 8,
    gap: 6,
    minHeight: 32,
    elevation: 2,
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
  },
  editButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: "#414042",
  },
  exportButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    borderWidth: 1,
    borderColor: "#CBD5E1",
    paddingHorizontal: 12,
    paddingVertical: 5.5,
    borderRadius: 8,
    gap: 6,
    minHeight: 32,
    elevation: 2,
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
  },
  exportButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: "#414042",
  },

  // Main Content Row
  mainContentRow: {
    flexDirection: "row-reverse",
    gap: 24,
    marginBottom: 32,
  },
  mainContentRowSmall: {
    flexDirection: "column",
  },

  // Donut Section
  donutSection: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 24,
    alignItems: "center",
    width: 240,
  },
  donutSectionSmall: {
    width: "100%",
  },
  sectionLabel: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: "#414042",
    marginBottom: 20,
  },
  donutLegendItems: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 12,
    marginTop: 16,
  },
  donutLegendRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  donutLegendDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  donutLegendLabel: {
    fontSize: 11,
    fontFamily: FONTS.regular,
  },

  // KPI Distribution Wrapper
  kpiDistributionWrapper: {
    flex: 1,
  },
  kpiDistributionWrapperSmall: {
    width: "100%",
  },

  // Basic Info Section
  basicInfoSection: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 24,
  },
  basicInfoSectionSmall: {
    width: "100%",
  },
  basicInfoTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: "#414042",
    textAlign: "right",
    marginBottom: 20,
  },
  infoGrid: {
    gap: 16,
  },
  infoRow: {
    flexDirection: "row-reverse",
    gap: 24,
  },
  infoItem: {
    flex: 1,
    backgroundColor: "#f1f5f9",
    borderRadius: 8,
    padding: 16,
  },
  infoLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: "#6B7280",
    textAlign: "right",
    marginBottom: 8,
  },
  infoValue: {
    fontSize: 14,
    fontFamily: FONTS.numbers,
    fontWeight: "500",
    color: "#414042",
    textAlign: "right",
  },

  // Process Connections Card (ارتباطات العملية)
  connectionsCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    padding: 12,
    marginBottom: 24,
    gap: 16,
    elevation: 2,
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
  },
  connectionsCardTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: "#414042",
    textAlign: "right",
  },

  // Section (Legacy)
  section: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 24,
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: "#414042",
  },
  subSectionTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: "#414042",
    textAlign: "right",
    marginBottom: 16,
  },
  addSubProcessButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F1F5F9",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    gap: 6,
  },
  addSubProcessText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: "#414042",
  },

  // Strategic Goals Section
  strategicGoalsSection: {
    backgroundColor: "#F1F5F9",
    borderRadius: 8,
    padding: 12,
    gap: 10,
  },
  strategicGoalsSectionHeader: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  strategicGoalsSectionTitle: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: "#414042",
    textAlign: "right",
  },
  strategicGoalsGrid: {
    flexDirection: "row-reverse",
    flexWrap: "wrap",
    gap: 20,
    marginTop: 10,
  },
  strategicGoalsGridSmall: {
    gap: 12,
  },
  strategicGoalCard: {
    width: "23.5%",
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    padding: 12,
    gap: 16,
  },
  strategicGoalCardSmall: {
    width: "100%",
  },
  strategicGoalCardContent: {
    gap: 16,
  },
  strategicGoalCardTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: "#414042",
    textAlign: "right",
  },

  // Sub-Processes Section (Figma matching)
  subProcessesSection: {
    backgroundColor: "#F1F5F9",
    borderRadius: 8,
    padding: 12,
    gap: 10,
  },
  subProcessesSectionHeader: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 20,
  },
  subProcessesSectionTitle: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: "#414042",
    textAlign: "right",
  },
  addSubProcessBtn: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    borderWidth: 1,
    borderColor: "#CBD5E1",
    paddingHorizontal: 12,
    paddingVertical: 5.5,
    borderRadius: 8,
    gap: 6,
    minHeight: 32,
    elevation: 2,
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
  },
  addSubProcessBtnText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: "#414042",
  },
  subProcessCardsGrid: {
    flexDirection: "row-reverse",
    flexWrap: "wrap",
    gap: 20,
    marginTop: 10,
  },
  subProcessCardsGridSmall: {
    gap: 12,
  },
  subProcessCardNew: {
    width: "23.5%",
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    padding: 12,
    gap: 16,
  },
  subProcessCardNewSmall: {
    width: "100%",
  },
  subProcessCardContent: {
    gap: 16,
  },
  subProcessCardTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: "#414042",
    textAlign: "right",
  },
  subProcessProgressRow: {
    flexDirection: "row-reverse",
    alignItems: "center",
    gap: 8,
  },
  progressBadge: {
    backgroundColor: "#F1F5F9",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 100,
  },
  progressBadgeText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: "#414042",
    textAlign: "center",
  },
  progressBarContainer: {
    flex: 1,
    height: 8,
    backgroundColor: "#F1F5F9",
    borderRadius: 8,
    position: "relative",
    overflow: "visible",
    flexDirection: "row-reverse",
  },
  progressBarFill: {
    height: 8,
    backgroundColor: "#2CA02C",
    borderRadius: 8,
  },
  progressDot: {
    position: "absolute",
    top: -3,
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: "#2CA02C",
    borderWidth: 2,
    borderColor: "#FCFCFC",
  },
  viewDetailsBtn: {
    flexDirection: "row-reverse",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  viewDetailsBtnText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: "#414042",
    textAlign: "right",
  },
  emptyStateContainer: {
    padding: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyStateText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: "#64748B",
    textAlign: "center",
  },

  // Cards Grid (Legacy)
  cardsGrid: {
    flexDirection: "row-reverse",
    flexWrap: "wrap",
    gap: 16,
  },
  cardsGridSmall: {
    gap: 12,
  },

  // Sub-Process Card (Legacy)
  subProcessCard: {
    width: "23%",
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 16,
  },
  subProcessCardSmall: {
    width: "100%",
  },
  subProcessTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: "#414042",
    textAlign: "right",
    marginBottom: 12,
    minHeight: 40,
  },
  subProcessProgress: {
    flexDirection: "row-reverse",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
  },
  progressText: {
    fontSize: 12,
    fontFamily: FONTS.medium,
    color: "#414042",
  },
  viewDetailsLink: {
    flexDirection: "row-reverse",
    alignItems: "center",
    gap: 4,
  },
  viewDetailsText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: "#6B7280",
  },

  // Horizontal scroll container for KPI cards
  horizontalScrollContainer: {
    flexGrow: 0,
    marginTop: 10,
  },
  // RTL wrapper - flips the entire ScrollView horizontally so cards start from right
  rtlWrapper: {
    transform: [{ scaleX: -1 }],
  },
  // RTL card - flips each card back to normal orientation
  rtlCard: {
    transform: [{ scaleX: -1 }],
  },
  horizontalCardsRow: {
    flexDirection: "row",
    gap: 16,
    paddingHorizontal: 4,
    paddingBottom: 12,
  },

  // KPI Card
  kpiCard: {
    width: "48%",
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 16,
  },
  kpiCardSmall: {
    width: "100%",
  },
  kpiTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: "#414042",
    textAlign: "right",
    marginBottom: 12,
    minHeight: 40,
  },
  kpiProgress: {
    flexDirection: "row-reverse",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
  },
  kpiProgressText: {
    fontSize: 12,
    fontFamily: FONTS.medium,
    color: "#414042",
    minWidth: 70,
    textAlign: "left",
  },

  // KPIs Section (Figma matching - same as sub-processes)
  kpisSection: {
    backgroundColor: "#F1F5F9",
    borderRadius: 8,
    padding: 12,
    gap: 10,
  },
  kpisSectionHeader: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 20,
  },
  kpisSectionTitle: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: "#414042",
    textAlign: "right",
  },
  addKpiBtn: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    borderWidth: 1,
    borderColor: "#CBD5E1",
    paddingHorizontal: 12,
    paddingVertical: 5.5,
    borderRadius: 8,
    gap: 6,
    minHeight: 32,
    elevation: 2,
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
  },
  addKpiBtnText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: "#414042",
  },

  // KPI Card New (2 per row - wider than sub-process cards)
  kpiCardNew: {
    width: 288, // Fixed width (w-72)
    flexShrink: 0, // Prevent shrinking
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    padding: 12,
    gap: 16,
  },
  kpiCardContent: {
    gap: 16,
  },
  kpiCardTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: "#414042",
    textAlign: "right",
    minHeight: 40,
  },
  kpiProgressRow: {
    flexDirection: "row-reverse",
    alignItems: "center",
    gap: 8,
  },
  kpiProgressBadge: {
    backgroundColor: "#F1F5F9",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 100,
  },
  kpiProgressBadgeText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: "#414042",
    textAlign: "center",
  },
  kpiProgressBarContainer: {
    flex: 1,
    height: 8,
    backgroundColor: "#F1F5F9",
    borderRadius: 8,
    position: "relative",
    overflow: "visible",
    flexDirection: "row-reverse",
  },
  kpiProgressBarFill: {
    height: 8,
    borderRadius: 8,
  },
  kpiProgressDot: {
    position: "absolute",
    top: -3,
    width: 14,
    height: 14,
    borderRadius: 7,
    borderWidth: 2,
    borderColor: "#FCFCFC",
  },
  kpiViewDetailsBtn: {
    flexDirection: "row-reverse",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  kpiViewDetailsBtnText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: "#414042",
    textAlign: "right",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  modalContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 24,
    width: "100%",
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 18,
    fontFamily: FONTS.medium,
    fontWeight: "600",
    color: "#1F2937",
    textAlign: "right",
    marginBottom: 8,
  },
  modalMessage: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: "#6B7280",
    textAlign: "right",
    marginBottom: 16,
  },
  modalButtons: {
    flexDirection: "row",
    gap: 12,
    marginTop: 16,
  },
  modalCancelButton: {
    flex: 1,
    backgroundColor: "#F3F4F6",
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: "center",
  },
  modalCancelButtonText: {
    fontFamily: FONTS.medium,
    fontSize: 14,
    color: "#6B7280",
  },
  modalRejectButton: {
    flex: 1,
    backgroundColor: "#EF4444",
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: "center",
  },
  modalRejectButtonText: {
    fontFamily: FONTS.medium,
    fontSize: 14,
    color: "#FFFFFF",
  },
  rejectionReasonInput: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    marginVertical: 16,
    minHeight: 100,
    fontFamily: FONTS.regular,
    fontSize: 14,
    textAlignVertical: "top",
  },
});
